import { useEffect, useState } from 'react'
import { Navigate, Outlet, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../shared/store/auth.store.js'
import { isPlatformAdminRole } from '../shared/utils/permissions.js'
import { ROUTES } from './routes.js'
import type { PlatformOutletContext } from './platform-context.js'
import LoadingPage from '../pages/loading/LoadingPage.js'

export default function PlatformGuard() {
  const { t } = useTranslation()
  const status = useAuthStore(s => s.status)
  const user = useAuthStore(s => s.user)
  const profileStatus = useAuthStore(s => s.profileStatus)
  const profileError = useAuthStore(s => s.profileError)
  const refreshProfile = useAuthStore(s => s.refreshProfile)
  const logout = useAuthStore(s => s.logout)
  const [checked, setChecked] = useState(false)

  useEffect(() => {
    if (status !== 'authenticated' || checked) return
    void refreshProfile().finally(() => setChecked(true))
  }, [status, checked, refreshProfile])

  if (status === 'initializing') return <LoadingPage />
  if (status !== 'authenticated') return <Navigate to={ROUTES.LOGIN} replace />

  if (!checked || profileStatus === 'loading') return <LoadingPage />

  if (profileStatus === 'error') {
    return (
      <div className="app-layout platform-layout">
        <main className="app-main">
          <section className="platform-message" role="alert">
            <h1>{t('platform.profileError.title')}</h1>
            <p>{t('platform.profileError.description')}</p>
            {profileError && <p className="field-hint">{profileError}</p>}
            <div className="form-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => void refreshProfile()}
              >
                {t('error.retry')}
              </button>
              <Link className="btn btn-ghost" to={ROUTES.DASHBOARD}>
                {t('platform.backToWorkspace')}
              </Link>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => void logout()}
              >
                {t('app.logout')}
              </button>
            </div>
          </section>
        </main>
      </div>
    )
  }

  const role = user?.platformRole
  if (!isPlatformAdminRole(role)) {
    return (
      <div className="app-layout platform-layout">
        <main className="app-main">
          <section className="platform-message" role="alert">
            <h1>{t('platform.accessDenied.title')}</h1>
            <p>{t('platform.accessDenied.description')}</p>
            <div className="form-actions">
              <Link className="btn btn-primary" to={ROUTES.DASHBOARD}>
                {t('platform.backToWorkspace')}
              </Link>
              <Link className="btn btn-ghost" to={ROUTES.HOME}>
                {t('platform.backHome')}
              </Link>
            </div>
          </section>
        </main>
      </div>
    )
  }

  return <Outlet context={{ platformRole: role } satisfies PlatformOutletContext} />
}
